import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  extractNumberArr,
  getCurrencySymbol,
  getPaymentTotalAmount,
  getPaymentTotalValue,
  HxOrganizationService,
  HxReportService,
  HxStoreService,
  HxUiLabelPipe,
  isoDate,
  OrganizationModel,
  PaymentType,
  ReportProductModel,
  ReportTotalInfo,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { DataService } from '@manager-app/service/data.service';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { saveAs } from 'file-saver';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxDateRangeSelectComponent, HxEnumSelectComponent, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';

interface ReportProductItem extends ReportProductModel {
  totalAmount?: number;
  totalValue?: number;
}

/**
 * Отчёт по продажам
 */
@Component({
    selector: 'app-revenue-report.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './revenue-report.component.html',
    styleUrls: ['./revenue-report.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxEnumSelectComponent, HxProductCategorySelectComponent, NgSelectComponent, NgIf, NgbTooltip, NgClass, NgFor, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class RevenueReportComponent implements OnInit, OnDestroy {
  PaymentType = PaymentType;
  productRevenueList: ReportProductItem[] = [];
  store?: StoreBasicModel;
  organization?: OrganizationModel;
  limits = DataService.limits;
  pagination = {
    allItemCount: 0
  };
  isLoading = {
    list: false,
    generate: false
  };
  columns: PaymentType[] = [];
  reportTotalInfo?: ReportTotalInfo;

  date?: DateRange;
  params: {
    date?: DateRange;
    limit?: number;
    categoryIds?: number[];
    paymentTypes?: PaymentType[],
    page?: number,
    storeIds?: number[],
  } = {};
  brandId?: number;
  storeCurrencyMap = new Map<number, string>();
  storeMap = new Map<number, StoreBasicModel>();
  private sub: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    public modal: NgbModal,
    private orgService: HxOrganizationService,
    private errorService: ErrorHandlerService,
    private toastr: HcToastrService,
    private storeService: HxStoreService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name) ? paramMap.get(name).split(',').map(mapFn) : undefined;
      this.params.paymentTypes = asArrFn('paymentTypes', str => PaymentType[str]) ?? [];
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap);
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(Date.now()), to: isoDate(Date.now())};
      this.params.storeIds = asArrFn('storeIds', id => Number(id)) ?? extractDefaultStoreIds();
      if (this.params.storeIds.length > 0) {
        this.loadStoreAndReport();
      } else {
        this.productRevenueList = [];
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  applyFilters() {
    this.pageChanged(1);
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (!store) {
      this.params.categoryIds = [];
      this.params.storeIds = [];
    }
    this.brandId = store[0]?.brandId;
    this.applyFilters();
  }

  generate() {
    this.isLoading.generate = true;
    this.reportService.generateProductRevenue(this.params).subscribe(response => {
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('revenue-report.ts.success'));
      this.isLoading.generate = false;
    }, () => this.isLoading.generate = false);
  }

  goBack(): void {
    this.router.navigateByUrl('/reports');
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadProductRevenues() {
    this.productRevenueList = [];
    this.isLoading.list = true;
    this.reportService.productRevenue(this.params).subscribe(response => {
      this.columns = response.columns;
      const totalInfo = response.totalInfo;
      totalInfo.payments = totalInfo.payments.filter(p => this.columns.includes(p.type));
      this.reportTotalInfo = totalInfo;
      this.productRevenueList = response.list.map(pr => {
        const item: ReportProductItem = pr;
        item.totalAmount = getPaymentTotalAmount(item.payments);
        item.totalValue = getPaymentTotalValue(item.payments);
        item.payments = item.payments.filter(p => this.columns.includes(p.type));
        return item;
      });
      this.pagination.allItemCount = response.count;
      this.isLoading.list = false;
    }, error => {
      this.isLoading.list = false;
      this.errorService.handler(error);
    });
  }

  private loadCurrentOrganization() {
    this.orgService.getOrganizationById(this.store.organizationId).then(organization => {
      this.organization = organization;
    });
  }

  private loadStoreAndReport() {
    this.storeMap.clear();
    this.storeCurrencyMap.clear();
    this.store = undefined;
    if (this.params.storeIds.length > 1) {
      this.storeService.getStoreByIds(this.params.storeIds).then(stores => {
        stores.forEach(st => {
          this.storeCurrencyMap.set(st.id, getCurrencySymbol(st.currency));
          this.storeMap.set(st.id, st);
        });
      });
      this.loadProductRevenues();
    } else {
      this.storeService.getFullStore(this.params.storeIds[0]).then(store => {
        this.store = store;
        this.brandId = store.brandId;
        this.storeCurrencyMap.set(store.id, getCurrencySymbol(store.currency));
        this.storeMap.set(store.id, store);
        this.loadCurrentOrganization();
        this.loadProductRevenues();
      });
    }
  }
}
